/**
 * Shared utility functions for Contract Manager components
 */

const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]

/**
 * Formats a date string to a readable format (e.g., "Jan 15, 2024")
 */
export const coiReviewCaption = (coi: {
    status?: string | null
    reviewed_by?: { name?: string | null } | null
    reviewed_at?: string | null
}) => {
    const name = coi.reviewed_by?.name
    if (!name) return null
    const action =
        coi.status === 'COMPLIANT'
            ? 'Approved'
            : coi.status === 'NON_COMPLIANT'
            ? 'Denied'
            : 'Reviewed'
    const when = coi.reviewed_at ? formatDate(coi.reviewed_at) : ''
    return when && when !== '—'
        ? `${action} by ${name} on ${when}`
        : `${action} by ${name}`
}

export const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—'
    const [datePart] = dateString.split('T')
    const [year, month, day] = datePart.split('-')
    return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${parseInt(
        day,
        10,
    )}, ${year}`
}

/**
 * Formats a number or string to USD currency format
 */
export const formatCurrency = (
    value: number | string | null | undefined,
): string => {
    if (value === null || value === undefined) return '—'
    const num =
        typeof value === 'string'
            ? parseFloat(value.replace(/[^0-9.-]/g, ''))
            : value
    if (isNaN(num)) return '—'
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num)
}

/**
 * Parses a currency string back to a number
 */
export const parseCurrencyToNumber = (value: string): number | null => {
    const num = parseFloat(value.replace(/[^0-9.-]/g, ''))
    return isNaN(num) ? null : num
}

/**
 * Gets initials from a name (e.g., "John Doe" -> "JD")
 */
export const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
}

/**
 * Formats file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
